import {computed, effect, inject} from '@angular/core';
import {patchState, signalStore, withComputed, withHooks, withMethods, withState} from '@ngrx/signals';
import {rxMethod} from '@ngrx/signals/rxjs-interop';
import {concatMap, of, pipe, switchMap, take, tap} from 'rxjs';
import {tapResponse} from '@ngrx/operators';
import {ApiSession} from '../services/api.session';
import {ApiOrder} from "../services/api.order";
import {BillResponse, Order} from '../core/models';
import {StorageService} from '../services/storage.service';
import {SESSION_KEY} from '../core/constants';

export interface SessionState {
  tableUID: string;
  selectedTable: string | null;
  customerName: string;
  activeOrders: Order[];
  orderRestaurantId: string | null;
  customerId: string | null;
  tableId: string | null;
  ticketNumber: number | null;
  isSessionLoading: boolean;
  orderDetails: BillResponse | null;
  shortReference: string | null;
  sessionId: string | null;
}

const loadSessionFromStorage = (): Partial<SessionState> => {
  const saved = localStorage.getItem(SESSION_KEY);
  if (saved) {
    const parsed = JSON.parse(saved);
    return {...parsed, activeOrders: parsed.activeOrders || []};
  }
  return {customerId: null, activeOrders: []};
};

const initialSessionState: SessionState = {
  tableUID: '',
  selectedTable: null,
  customerName: '',
  activeOrders: [],
  isSessionLoading: false,
  orderDetails: null,
  orderRestaurantId: null,
  customerId: null,
  tableId: null,
  ticketNumber: 0,
  shortReference: null,
  sessionId: null,
  ...loadSessionFromStorage(),
};

export const SessionStore = signalStore(
  {providedIn: 'root'},
  withState<SessionState>(initialSessionState),
  withComputed((store) => ({
    isOrderLocked: computed(() => {
      const details = store.orderDetails()?.details;
      if (!Array.isArray(details)) return false;

      const customerId = store.customerId();
      return details.some((d: any) =>
        d.customerId === customerId && d.statusOrder === 'BILL_REQUESTED'
      );
    })
  })),
  withMethods((
    store,
    apiSession = inject(ApiSession),
    apiOrder = inject(ApiOrder)
  ) => ({
    setOderRestaurant: (orderId: BillResponse) => patchState(store, {orderDetails: orderId}),
    setTable: (tableId: string) => patchState(store, {tableUID: tableId}),
    setCustomerName: (name: string) => patchState(store, {customerName: name}),
    updateActiveOrders: (orders: Order[]) => patchState(store, {activeOrders: orders}),
    resetSession: () => patchState(store, initialSessionState),


    // Centraliza la lógica de mutación de identidad resguardando los flujos del checkout
    updateCustomerSessionName: (name: string): void => {
      patchState(store, {customerName: name});
    },

    initializeSession: rxMethod<{ onSuccess: () => void, onError: (err: any) => void }>(
      pipe(
        tap(() => patchState(store, {isSessionLoading: true})),
        switchMap(({onSuccess, onError}) => {
          const tableUID = store.tableUID();
          const name = store.customerName() || 'Cliente';
          return apiSession.initializeSession(tableUID, name).pipe(
            tapResponse({
              next: (res) => {
                patchState(store, {...res, tableUID, isSessionLoading: false});
                onSuccess();
              },
              error: (err) => {
                patchState(store, {isSessionLoading: false});
                onError(err);
              }
            })
          );
        })
      )
    ),

    reloadSession: rxMethod<{ customerId: string | null, onSuccess: () => void, onError: () => void }>(
      pipe(
        switchMap(({customerId, onSuccess, onError}) => {
          if (!customerId) {
            onError();
            return of(null);
          }
          return apiSession.reloadApp(customerId).pipe(
            tap((response) => {
              const {tableId: tableUID} = response;
              patchState(store, {...response, tableUID});
              const orderId = store.orderRestaurantId();
              const currentCustomerId = store.customerId();
              if (!orderId || !currentCustomerId) {
                onError();
              } else {
                onSuccess();
              }
            })
          );
        })
      )
    ),

    loadOrderStatusSession: rxMethod<void>(
      pipe(
        concatMap(() => {
          const orderId = store.orderRestaurantId();
          const customerId = store.customerId();
          if (!orderId || !customerId) return of(null);
          return apiOrder.getOrderStatus(orderId, customerId).pipe(
            take(1),
            tapResponse({
              next: (details) => patchState(store, {orderDetails: details}),
              error: (err) => console.error('Error al cargar seguimiento:', err)
            })
          );
        })
      )
    ),
    cancelProduct: rxMethod<{ orderProductId: number, onSuccess: () => void, onError: (err: any) => void }>(
      pipe(
        switchMap(({orderProductId, onSuccess, onError}) =>
          apiOrder.cancelOrderItem(orderProductId).pipe(
            tapResponse({
              next: () => onSuccess(),
              error: (err) => onError(err)
            })
          )
        )
      )
    ),

    requestFinalBill: rxMethod<{ onSuccess: () => void, onError: (err: any) => void }>(
      pipe(
        switchMap(({onSuccess, onError}) =>
          apiOrder.requestBill(store.orderRestaurantId() || '', store.customerId() || '').pipe(
            tapResponse({
              next: () => onSuccess(),
              error: (err) => onError(err)
            })
          )
        )
      )
    ),

    callWaiter: rxMethod<{ onSuccess: () => void, onError: (err: any) => void }>(
      pipe(
        switchMap(({onSuccess, onError}) =>
          apiOrder.callWaiter(store.customerId() || '').pipe(
            tapResponse({
              next: () => onSuccess(),
              error: (err) => onError(err)
            })
          )
        )
      )
    ),
  })),
  withHooks({
    onInit(store, storageService = inject(StorageService)) {
      effect(() => {
        const user = {
          tableUID: store.tableUID(),
          selectedTable: store.tableId(),
          tableNumber: store.tableId(),
          name: store.customerName(),
        };
        storageService.setUserSession(JSON.stringify(user));
        storageService.setItemSession(JSON.stringify({
          customerId: store.customerId(),
          activeOrders: store.activeOrders(),
          orderRestaurantId: store.orderRestaurantId()
        }));
      });
    },
  })
);
